import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import AppName from 'src/components/AppName/AppName'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RegisterAccountBinInputForm from 'src/components/RegisterAccountBinInputForm/RegisterAccountBinInputForm'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import { SupportedBrands } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import { registerAccountUrl } from 'src/navigation/urls'
import { getCreditCardsActiveSourceCountStatistics } from 'src/redux/actions/data/statistics.actions'
import { selectAllStatisticsLoadedFromState } from 'src/redux/selectors/app.selectors'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

interface RegisterAccountBinInputPageProps {
  flatFields: AccountBinInputPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof AccountBinInputPageFlatFields,
    value: AccountBinInputPageFlatFields[keyof AccountBinInputPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  cvcFlatFields: AccountBinInputPageCvcFlatFields
  onCvcFlatFieldChange: (
    fieldName: keyof AccountBinInputPageCvcFlatFields,
    value: AccountBinInputPageCvcFlatFields[keyof AccountBinInputPageCvcFlatFields],
  ) => void
  cvcFlatFieldsErrors: Partial<
    Record<
      keyof AccountBinInputPageCvcFlatFields,
      Array<React.ReactElement | string>
    >
  >
  loading?: boolean
  onSubmit: (bin?: string) => void
  hasOrganization: boolean
  topLevelError?: string
}

const RegisterAccountBinInputPage: React.FC<
  RegisterAccountBinInputPageProps
> = ({
  flatFields,
  onFlatFieldChange,
  flatFieldsErrors,
  cvcFlatFields,
  onCvcFlatFieldChange,
  cvcFlatFieldsErrors,
  onSubmit,
  hasOrganization,
  topLevelError,
  loading,
}): React.ReactElement => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const translate = useTranslate()
  const { whiteLabelIncludes } = useWhitelabel()

  const dispatch = useBackgroundDispatch()

  let binInputTitle = translate('Add your company credit card')
  let binInputSubtitle = translate(
    'Enter your company credit card details in order to track expenses and control spending',
  )

  if (whiteLabelIncludes(SupportedBrands.Jpm)) {
    binInputTitle = hasOrganization
      ? translate('Register an Account')
      : translate('Add an account')

    binInputSubtitle = hasOrganization
      ? translate(
          "Provide a payment method for your account. This credit card or bank account will be used to pay for expenses made with Virtual Cards. Let's start with your card information.",
        )
      : translate(
          "Provide a funding source for your account. This credit card or bank account will be used to pay for expenses made with Virtual Cards. Let's start with your card information.",
        )
  }

  useEffect((): void => {
    dispatch(getCreditCardsActiveSourceCountStatistics())
  }, [])

  return !statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <RegisterAccountBinInputForm
        flatFields={flatFields}
        onFlatFieldChange={onFlatFieldChange}
        flatFieldsErrors={flatFieldsErrors}
        cvcFlatFields={cvcFlatFields}
        onCvcFlatFieldChange={onCvcFlatFieldChange}
        cvcFlatFieldsErrors={cvcFlatFieldsErrors}
        onSubmit={onSubmit}
        title={binInputTitle}
        subtitle={binInputSubtitle}
        cardInfoHeader={
          <div
            css={css`
              margin-bottom: 20px;
            `}
          >
            <span
              id={'cardHeader'}
              css={css`
                ${getFontCss(
                  20,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <Trns source={'Card Information'} />
            </span>
            <div
              css={css`
                margin-top: 8px;
                display: flex;
                flex-direction: row;
              `}
            >
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <SvgLocked
                  css={css`
                    transform-origin: left;
                    transform: scale(calc(5 / 7));
                  `}
                />
                <Trns
                  source={
                    'Security is our top priority. We meet or exceed security standards to handle your card information.'
                  }
                  expressions={{
                    appName: <AppName />,
                  }}
                />
              </span>
            </div>
          </div>
        }
        topLevelError={topLevelError}
        loading={loading}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background-color: ${styleConstants.colors.gray_light};
          padding: 40px 60px;
          border-radius: 0px 0px 10px 10px;
        `}
      >
        <p
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={
              "Need help? Don't have a valid card number with your bank? Visit our ${resourceCenter} for support and contact options."
            }
            expressions={{
              resourceCenter: (
                <ResourceCenterLink
                  underline
                  size={HoverableLinkSizes.Large}
                  analytics={{
                    event: 'register account - card info - contact support',
                    properties: {
                      intent: 'view "contact support"',
                      interaction: 'text link press',
                      screen: registerAccountUrl(),
                    },
                  }}
                />
              ),
            }}
          />
        </p>
      </div>
    </>
  )
}

export default RegisterAccountBinInputPage
